import { useMemo } from "react";
import {
  BarDatum,
  BarTooltipProps,
  ComputedDatum,
  ResponsiveBar,
} from "@nivo/bar";
import {
  buildKeysFromSelection,
  buildNiceTicksMax5,
  buildTickValues,
  CHART_MARGIN_BOTTOM,
  CHART_MARGIN_TOP,
  getActualValue,
  getMinDisplayValue,
  getTickValuesFormat,
} from "utils/ChartUtil";
import { ChartTooltip } from "./ChartTooltip";
import { BarGraphProps, RowsData } from "api/interfaces/chartInterface.interface";
import { useAppSelector } from "redux/hooks";
import { COLORS } from "styles/colors";
import { calcRem } from "styles/theme";
import styled from "styled-components";
import { WithTooltip } from "components/blocks/WithTooltip";
import GridYAxis from "./GridYAxis";

export default function OrgStackedBarChart({
  rows,
  activeOrgs,
  width,
  items,
  groupMode = "stacked",
  unit = "number",
  indexBy = "date",
  limitLineTop,
  ...rest
}: BarGraphProps) {
  const keys = useMemo(
    () => buildKeysFromSelection(activeOrgs, items),
    [activeOrgs, items]
  );
  const theme = useAppSelector((state) => state.theme.theme);
  const tickColor =
    theme === "light" ? `${COLORS.BLACK}80` : `${COLORS.WHITE}80`;
  const LineColor =
    theme === "light" ? `${COLORS.BLACK}20` : `${COLORS.WHITE}20`;
  const colorByKey = useMemo(() => {
    const map = new Map<string, string>();
    items.forEach((c) => map.set(c.key, c.color));
    return map;
  }, [items]);

  const sum = useMemo(() => {
    return (rows as { value?: number }[]).reduce(
      (acc, curr) => acc + Number(curr.value ?? 0),
      0
    );
  }, [rows]);

  const tickValues = useMemo(
    () =>
      buildTickValues(
        rows.map((r) => String((r as BarDatum)[indexBy] ?? "")),
        width
      ),
    [rows, width, indexBy]
  );

  const tickValuesFormat = useMemo(() => {
    return (v: string) => getTickValuesFormat(tickValues, v);
  }, [tickValues, width]);

  const chartData = useMemo(() => {
    if (keys.length === 0) return rows;
    return getMinDisplayValue(
      rows as RowsData[],
      keys,
      groupMode
    ) as BarDatum[];
  }, [rows, keys, groupMode]);
  const { tickValues: tickYValues, yMax } = useMemo(() => {
    if (keys.length === 0) return { tickValues: [0, 1], yMax: 1 };
    let maxY = 0;
    chartData.forEach((row: Record<string, unknown>) => {
      if (groupMode === "stacked") {
        const sum = keys.reduce(
          (s, k) => s + (Number((row as Record<string, unknown>)[k]) || 0),
          0
        );
        maxY = Math.max(maxY, sum);
      } else {
        keys.forEach((k) => {
          maxY = Math.max(
            maxY,
            Number((row as Record<string, unknown>)[k]) || 0
          );
        });
      }
    });
    if (maxY === 0) return { tickValues: [0, 1], yMax: 1 };
    const ticks = buildNiceTicksMax5(maxY);
    const maxTickValue = Math.max(...ticks);
    return { tickValues: ticks, yMax: maxTickValue };
  }, [chartData, keys, groupMode]);

  const barProps = useMemo(() => {
    return {
      ...rest,
      data: chartData as BarDatum[],
      keys,
      indexBy,
      margin: {
        top: CHART_MARGIN_TOP,
        right: 0,
        bottom: CHART_MARGIN_BOTTOM,
        left: 0,
      },
      padding: groupMode === "grouped" ? 0.3 : 0.7,
      innerPadding: 1,
      groupMode: groupMode as "stacked" | "grouped",
      colors: (bar: ComputedDatum<BarDatum>) =>
        colorByKey.get(bar.id as string) ?? "#999",
      borderRadius: rest.borderRadius ?? 2,
      enableGridY: rest.enableGridY ?? false,
      enableLabel: rest.enableLabel ?? false,
      axisTop: rest.axisTop ?? null,
      axisRight: rest.axisRight ?? null,
      axisLeft: rest.axisLeft ?? null,
      maxValue:
        rest.maxValue !== undefined
          ? rest.maxValue
          : rest.enableGridY
          ? yMax
          : "auto",
      gridYValues:
        rest.gridYValues !== undefined
          ? rest.gridYValues
          : rest.enableGridY
          ? tickYValues
          : undefined,
      minValue: rest.minValue ?? 0,
      layers: rest.layers ?? [
        "grid",
        "bars",
        "axes",
        "markers",
        "legends",
        "annotations",
      ],
      axisBottom: {
        ...rest.axisBottom,
        tickSize: 0,
        tickPadding: rest.axisBottom?.tickPadding ?? 12,
        tickValues: rest.axisBottom?.tickValues ?? tickValues,
        format: rest.axisBottom?.format ?? tickValuesFormat,
      },
      tooltip: (props: BarTooltipProps<BarDatum>) => {
        const actualValue = getActualValue(
          props.id as string,
          props.indexValue as string,
          rows as unknown as import("api/interfaces/widgetInterface.interface").WidgetStats[],
          indexBy
        );
        return (
          <ChartTooltip
            id={items.find((item) => item.key === props.id)?.label}
            value={actualValue}
            indexValue={props.indexValue as string}
            color={props.color}
            sum={sum}
            unit={unit}
          />
        );
      },
      theme: {
        ...rest.theme,
        axis: {
          ...rest.theme?.axis,
          ticks: {
            text: { fill: tickColor, fontSize: calcRem(12) },
          },
        },
        grid: {
          ...rest.theme?.grid,
          line: {
            stroke: rest.theme?.grid?.line?.stroke ?? LineColor,
            strokeWidth:
              rest.theme?.grid?.line?.strokeWidth ??
              (rest.enableGridY ? 0.5 : 0),
          },
        },
      },
      role: "application",
      ariaLabel: "Organization stacked bar chart",
    };
  }, [
    rest,
    chartData,
    keys,
    indexBy,
    groupMode,
    yMax,
    tickYValues,
    tickValues,
    tickValuesFormat,
    tickColor,
    LineColor,
    colorByKey,
    rows,
    items,
    sum,
    unit,
  ]);

  return (
    <GraphWrapper>
      {rest.enableGridY && (
        <GridYAxis
          gridYValues={rest.gridYValues}
          tickYValues={tickYValues}
          enableGridY={rest.enableGridY}
          unit={unit}
        />
      )}
      <GraphChartInner>
        {limitLineTop != null ? (
          limitLineTop.label ? (
            <WithTooltip tooltip={limitLineTop.label}>
              <LimitLineOverlay
                $top={limitLineTop.value}
                color={limitLineTop.color}
              />
            </WithTooltip>
          ) : (
            <LimitLineOverlay
              $top={limitLineTop.value}
              color={limitLineTop.color}
            />
          )
        ) : null}
        <ResponsiveBar {...barProps} />
      </GraphChartInner>
    </GraphWrapper>
  );
}

export const GraphChartInner = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  position: relative;
`;

export const GraphWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: 100%;
  height: 100%;
  gap: 10px;
`;

export const LimitLineOverlay = styled.div<{ $top: number; color?: string }>`
  position: absolute;
  left: 0;
  right: 0;
  top: ${({ $top }) => $top}px;
  border-top: 1px dashed
    ${({ theme, color }) =>
      color ??
      (theme.value === "light"
        ? theme.COLORS.SELECTED
        : `${theme.COLORS.WARNING}80`)};
  background-color: ${({ theme }) => theme.COLORS.BG};
  padding: 1px 0;
`;
